import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import styles from './Sidebar.module.css';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { friendsService } from '../../services/friends.service';
import { adminService } from '../../services/admin.service';
import { ACCOUNT_ITEMS, getMainSections, isNavItemActive, type NavItem } from './navigation';

const BADGE_REFRESH_MS = 3 * 60 * 1000;

/**
 * Main navigation: a fixed sidebar on the left on desktop (collapsible to icons),
 * a drawer opened by the header burger on mobile.
 */
export function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { collapsed, toggleCollapsed, mobileOpen, closeMobile } = useSidebar();
  const isAdmin = user?.role === 'admin';

  // Badges: refreshed every few minutes and when the user comes back to the tab.
  // Polling every 30 s kept every open tab hitting the API (and Render awake).
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['friends', 'pending-count'],
    queryFn: friendsService.getPendingRequestsCount,
    enabled: Boolean(user),
    refetchInterval: BADGE_REFRESH_MS,
    refetchOnWindowFocus: true,
  });

  // Unread activity logs (admins only)
  const { data: unreadLogCounts } = useQuery({
    queryKey: ['admin', 'unread-log-counts'],
    queryFn: adminService.getUnreadLogCounts,
    enabled: isAdmin,
    refetchInterval: BADGE_REFRESH_MS,
    refetchOnWindowFocus: true,
  });

  const totalUnreadLogs = useMemo(() => {
    if (!unreadLogCounts) return 0;
    return Object.values(unreadLogCounts).reduce((sum, count) => sum + count, 0);
  }, [unreadLogCounts]);

  const badgeCounts = { friends: pendingCount, admin: totalUnreadLogs };
  const sections = useMemo(() => getMainSections(isAdmin), [isAdmin]);

  // Close the mobile drawer when the page changes
  useEffect(() => {
    closeMobile();
  }, [location.pathname, location.search, closeMobile]);

  // Mobile drawer: Escape closes it and the page behind does not scroll
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    window.addEventListener('keydown', onKeyDown);
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      body.style.overflow = previousOverflow;
    };
  }, [mobileOpen, closeMobile]);

  const renderLink = (item: NavItem) => {
    const active = isNavItemActive(item, location);
    const count = item.badge ? badgeCounts[item.badge] : 0;
    const Icon = item.icon;
    return (
      <li key={item.id}>
        <Link
          to={item.to}
          className={`${styles.link} ${active ? styles.active : ''}`}
          aria-current={active ? 'page' : undefined}
          title={collapsed ? item.label : undefined}
        >
          <Icon size={20} className={styles.icon} aria-hidden="true" />
          <span className={styles.label}>{item.label}</span>
          {count > 0 && (
            <span className={styles.badge} aria-label={`${count} en attente`}>
              {count}
            </span>
          )}
        </Link>
      </li>
    );
  };

  const handleLogout = () => {
    closeMobile();
    void logout();
  };

  return (
    <>
      {/* Dimmed backdrop behind the drawer (mobile) */}
      <button
        type="button"
        className={`${styles.overlay} ${mobileOpen ? styles.overlayOpen : ''}`}
        onClick={closeMobile}
        aria-hidden={!mobileOpen}
        tabIndex={mobileOpen ? 0 : -1}
        aria-label="Fermer le menu"
      />

      <aside
        id="app-sidebar"
        className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${
          mobileOpen ? styles.mobileOpen : ''
        }`}
        aria-label="Navigation principale"
      >
        <button
          type="button"
          className={styles.collapseButton}
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Déployer la barre latérale' : 'Réduire la barre latérale'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Déployer' : 'Réduire'}
        >
          {collapsed ? (
            <PanelLeftOpen size={20} aria-hidden="true" />
          ) : (
            <PanelLeftClose size={20} aria-hidden="true" />
          )}
        </button>

        <nav className={styles.nav} aria-label="Sections">
          {sections.map((section) => (
            <div key={section.id} className={styles.section}>
              <p className={styles.sectionLabel}>{section.label}</p>
              <ul className={styles.list}>{section.items.map(renderLink)}</ul>
            </div>
          ))}
        </nav>

        <div className={styles.bottom}>
          <ul className={styles.list}>{ACCOUNT_ITEMS.map(renderLink)}</ul>
          <button
            type="button"
            className={`${styles.link} ${styles.logout}`}
            onClick={handleLogout}
            title={collapsed ? 'Se déconnecter' : undefined}
          >
            <LogOut size={20} className={styles.icon} aria-hidden="true" />
            <span className={styles.label}>Se déconnecter</span>
          </button>
        </div>
      </aside>
    </>
  );
}
